/**
 * ProviderReviewsPage — `/dashboard/provider/reviews`.
 *
 * Provider-scoped reviews surface. Pulls:
 *   GET /api/providers/{provider_profile.id}/aggregate  → avg + count
 *   GET /api/providers/{provider_profile.id}/reviews    → chrono list
 * Both gated server-side: provider sees ONLY their own data, admins
 * see anyone's. `is_provider` accounts whose provider_profile is
 * missing (shouldn't happen in v1) render a friendly empty state.
 *
 * Display rules:
 *   - Aggregate card: big avg number + StarRating display + count +
 *     5-bucket histogram (★1..★5 with bars).
 *   - List: chronological (server already DESCs by created_at), each
 *     row shows privacy-safe `customer_name` (server-attached),
 *     created_at, rating, comment (if any).
 *   - Hidden reviews ARE visible to the provider with a slate-on-light
 *     "Hidden by admin" pill (per N13 — aggregate math excludes them
 *     but the row is still surfaced for transparency).
 *   - Empty state: paw mascot + "No reviews yet."
 */
import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StarRating from "@/components/reviews/StarRating";
import { PawPrint, Star, EyeOff } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function HiddenPill() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-pp-text"
      data-testid="provider-reviews-hidden-pill"
    >
      <EyeOff className="h-3 w-3" aria-hidden="true" />
      Hidden by admin
    </span>
  );
}

function StarHistogram({ reviews }) {
  // Mirror the aggregate scope: only "visible" reviews are counted.
  const visible = useMemo(
    () => reviews.filter((r) => r.status === "visible"),
    [reviews]
  );
  const total = visible.length;
  const buckets = useMemo(() => {
    const map = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    visible.forEach((r) => {
      if (r.rating >= 1 && r.rating <= 5) map[r.rating] += 1;
    });
    return map;
  }, [visible]);

  return (
    <div
      className="flex flex-col gap-1.5"
      data-testid="provider-reviews-histogram"
    >
      {[5, 4, 3, 2, 1].map((star) => {
        const count = buckets[star];
        const pct = total > 0 ? (count / total) * 100 : 0;
        return (
          <div key={star} className="flex items-center gap-2 text-xs">
            <span className="flex w-8 items-center gap-0.5 font-semibold text-pp-text">
              {star}
              <Star className="h-3 w-3 fill-brand-orange text-brand-orange" />
            </span>
            <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-muted">
              <div
                className="absolute inset-y-0 left-0 bg-brand-orange"
                style={{ width: `${pct}%` }}
                data-testid={`provider-reviews-histogram-bar-${star}`}
              />
            </div>
            <span
              className="w-8 text-right font-mono text-pp-text"
              data-testid={`provider-reviews-histogram-count-${star}`}
            >
              {count}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function ProviderReviewsPage() {
  const { providerProfile } = useAuth();
  const providerId = providerProfile?.id;

  const [aggregate, setAggregate] = useState({ avg_rating: null, review_count: 0 });
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!providerId) {
      setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      try {
        const [{ data: agg }, { data: list }] = await Promise.all([
          api.get(`/providers/${providerId}/aggregate`),
          api.get(`/providers/${providerId}/reviews`),
        ]);
        if (!active) return;
        setAggregate(agg);
        setReviews(list);
      } catch (e) {
        toast.error(
          formatApiErrorDetail(e?.response?.data?.detail) || e.message
        );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [providerId]);

  return (
    <div className="mx-auto max-w-6xl" data-testid="provider-reviews-page">
      <header className="mb-6">
        <span className="text-sm font-bold uppercase tracking-wider text-brand-orange">
          Reviews
        </span>
        <h2 className="font-heading text-3xl font-bold text-pp-text">
          What customers say
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Ratings + comments from your completed Plucks. Aggregate math
          excludes any reviews an admin has hidden.
        </p>
      </header>

      <Card
        className="mb-6 rounded-3xl border-border bg-secondary p-8 shadow-none"
        data-testid="provider-reviews-aggregate"
      >
        <div className="grid gap-6 sm:grid-cols-[auto_1fr] sm:items-center">
          <div>
            <p className="text-sm font-bold uppercase tracking-wider text-pp-text">
              Average rating
            </p>
            <p
              className="mt-2 font-heading text-5xl font-bold text-pp-text sm:text-6xl"
              data-testid="provider-reviews-avg"
            >
              {aggregate.avg_rating === null
                ? "—"
                : aggregate.avg_rating.toFixed(1)}
            </p>
            <div className="mt-2">
              <StarRating
                value={aggregate.avg_rating || 0}
                readOnly
                size="md"
                testidPrefix="provider-reviews-avg-stars"
              />
            </div>
            <p
              className="mt-1 text-sm text-muted-foreground"
              data-testid="provider-reviews-count"
            >
              {aggregate.review_count}{" "}
              {aggregate.review_count === 1 ? "review" : "reviews"}
            </p>
          </div>
          <StarHistogram reviews={reviews} />
        </div>
      </Card>

      <Card
        className="rounded-3xl border-border bg-pp-surface p-6 shadow-none"
        data-testid="provider-reviews-list-card"
      >
        <h3 className="mb-4 font-heading text-lg font-semibold text-pp-text">
          All reviews
        </h3>

        {loading ? (
          <p
            className="rounded-2xl bg-muted/50 p-6 text-center text-sm text-muted-foreground"
            data-testid="provider-reviews-loading"
          >
            Loading…
          </p>
        ) : reviews.length === 0 ? (
          <div
            className="rounded-2xl bg-secondary p-8 text-center"
            data-testid="provider-reviews-empty"
          >
            <PawPrint className="mx-auto h-10 w-10 text-brand-orange" />
            <p className="mt-3 text-sm font-semibold text-pp-text">
              No reviews yet.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Complete more Plucks — customers will be prompted to rate them.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="provider-reviews-list">
            {reviews.map((r) => (
              <li
                key={r.id}
                className="rounded-2xl border border-border bg-pp-v3-bg p-4"
                data-testid={`provider-reviews-item-${r.id}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <StarRating
                      value={r.rating}
                      readOnly
                      size="sm"
                      testidPrefix={`provider-reviews-item-${r.id}-stars`}
                    />
                    <span className="text-sm font-semibold text-pp-text">
                      {r.customer_name || "Customer"}
                    </span>
                    {r.status === "hidden" && <HiddenPill />}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(r.created_at)}
                  </span>
                </div>
                {r.comment && (
                  <p
                    className="mt-2 text-sm text-pp-text"
                    data-testid={`provider-reviews-item-${r.id}-comment`}
                  >
                    {r.comment}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
